/**
 * Auth API client for login functionality
 *
 * Separate from main API client since login page doesn't have window.dddData
 */

import type {
	LoginRequest,
	LoginResponse,
	AuthCheckResponse,
	ForgotPasswordRequest,
	ForgotPasswordResponse,
	AuthErrorType,
} from '@/types';
import { AUTH_ROUTES } from './api-routes';
import i18n from './i18n';

// Translations outside components go through t(), the call the string extraction sees
const t = i18n.t.bind(i18n);

/**
 * Auth API Error with typed error code
 */
export class AuthAPIError extends Error {
	public errorType: AuthErrorType;
	public retryAfter?: number;

	constructor(message: string, errorType: AuthErrorType, retryAfter?: number) {
		super(message);
		this.name = 'AuthAPIError';
		this.errorType = errorType;
		this.retryAfter = retryAfter;
	}
}

/**
 * Auth API client
 */
class AuthAPIClient {
	private baseUrl: string;
	private nonce: string;

	constructor() {
		// Use login-specific data from PHP
		const loginData = window.dddLoginData;
		if (!loginData?.apiUrl || !loginData?.nonce) {
			throw new Error('Login data not available. Please refresh the page.');
		}
		this.baseUrl = loginData.apiUrl;
		this.nonce = loginData.nonce;
	}

	/**
	 * Update nonce (used after receiving new_nonce from API)
	 */
	public updateNonce(newNonce: string): void {
		this.nonce = newNonce;
	}

	/**
	 * Get current nonce
	 */
	public getNonce(): string {
		return this.nonce;
	}

	/**
	 * Parse error response and determine error type
	 */
	private parseErrorType(status: number, errorCode?: string): AuthErrorType {
		if (status === 429) {
			return 'rate_limited';
		}
		if (status === 403) {
			if (errorCode === 'rest_cookie_invalid_nonce') {
				return 'nonce_expired';
			}
			if (errorCode === 'license_inactive') {
				return 'license_inactive';
			}
			return 'no_capability';
		}
		if (status === 401 || errorCode === 'invalid_credentials') {
			return 'invalid_credentials';
		}
		return 'unknown';
	}

	/**
	 * Make authenticated API request
	 */
	private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		const url = `${this.baseUrl}${endpoint}`;

		const headers: HeadersInit = {
			'Content-Type': 'application/json',
			'X-WP-Nonce': this.nonce,
			'X-DDD-Locale': i18n.language,
			...options.headers,
		};

		try {
			const response = await fetch(url, {
				...options,
				headers,
				credentials: 'same-origin',
			});

			const data = await response.json().catch(() => ({}));

			if (!response.ok) {
				const errorType = this.parseErrorType(response.status, data.code);

				// Extract retry_after for rate limiting
				const retryAfter = data.retry_after;

				throw new AuthAPIError(
					data.message || `HTTP ${response.status}`,
					errorType,
					retryAfter
				);
			}

			return data as T;
		} catch (error) {
			if (error instanceof AuthAPIError) {
				throw error;
			}

			// Network error
			throw new AuthAPIError(
				t('Chyba siete. Skontrolujte pripojenie k internetu.'),
				'network_error'
			);
		}
	}

	/**
	 * Login user
	 */
	async login(data: LoginRequest): Promise<LoginResponse> {
		const response = await this.request<LoginResponse>(AUTH_ROUTES.login, {
			method: 'POST',
			body: JSON.stringify(data),
		});

		// Update nonce if new one is provided
		if (response.new_nonce) {
			this.updateNonce(response.new_nonce);
		}

		return response;
	}

	/**
	 * Logout user
	 */
	async logout(): Promise<{ success: boolean; redirect_url: string }> {
		return this.request(AUTH_ROUTES.logout, {
			method: 'POST',
		});
	}

	/**
	 * Check authentication status
	 */
	async checkAuth(): Promise<AuthCheckResponse> {
		const response = await this.request<AuthCheckResponse>(AUTH_ROUTES.check, {
			method: 'GET',
		});

		// Update nonce if new one is provided
		if (response.new_nonce) {
			this.updateNonce(response.new_nonce);
		}

		return response;
	}

	/**
	 * Request password reset
	 */
	async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
		return this.request(AUTH_ROUTES.forgotPassword, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	/**
	 * Validate password reset key
	 */
	async validateResetKey(data: {
		key: string;
		login: string;
	}): Promise<{ valid: boolean; user_name?: string }> {
		return this.request(AUTH_ROUTES.validateResetKey, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	/**
	 * Set new password after reset
	 */
	async setPassword(data: {
		key: string;
		login: string;
		password: string;
	}): Promise<{ success: boolean; message?: string; redirect_url?: string }> {
		return this.request(AUTH_ROUTES.setPassword, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}
}

// Export singleton instance
export const authApi = new AuthAPIClient();
